import {z} from "zod";

/**
 * Low-level Firestore data-fix marker — NOT a domain concept.
 *
 * Stamped on a document whose id used a non-canonical key form (e.g. a bare
 * user uid) once its state has been collapsed into the canonical sibling doc.
 * Readers treat a marked doc as retired; collapse-aware triggers early-return
 * on it so a collapse doesn't fire removal fan-out. See the permissions README
 * section "_docid_superseded" for the full rationale.
 */
export const DocIdSuperseded = z.object({
    /** Canonical doc id this entry was collapsed into (a prefixed user uid today). */
    by: z.string(),
    /** Timestamp in millis when the collapse happened. */
    on: z.number(),
});
export type DocIdSuperseded = z.infer<typeof DocIdSuperseded>;

/** Firestore field name for the {@link DocIdSuperseded} marker. */
export const DOC_ID_SUPERSEDED_FIELD = "_docid_superseded";

/**
 * True only for a well-formed marker pointing at a canonical sibling. Readers and
 * collapse-aware triggers MUST gate on this rather than mere field presence, so a
 * malformed/empty value (`null`, `{}`, missing `by`) never silently retires a live doc.
 */
export function isDocIdSuperseded(value: unknown): value is DocIdSuperseded {
    const parsed = DocIdSuperseded.safeParse(value);
    return parsed.success && parsed.data.by.length > 0;
}
